/**
 * AddPersonAgreementRoleModal — Phase 4b-2 commit 4 (2026-06-16, #11).
 *
 * The INVERSE of AddAgreementPartyModal. Same relationship, opposite
 * orientation: here THIS PERSON is fixed and the user picks the AGREEMENT
 * (+ role + role-scoped fields). Opened from PersonDetailPage's Roles tab
 * ("Roles on agreements" → "+ Add role"), it stages a CREATE op onto the
 * page's pending-party queue; the batch commits through maker-checker when
 * the user clicks Save Changes (approach A1 — the page is one transaction).
 *
 * Field scope mirrors AddAgreementPartyModal exactly (§3f modal-completeness,
 * Pedro 2026-06-16): Agreement, Role (HOLDER / REPRESENTATIVE / BENEFICIARY),
 * REPRESENTATIVE power type (LPOA / MANDATO — the backend CHECK rejects a
 * representative without one), UBO toggle (only for BENEFICIARY on an Empresa
 * agreement — the backend 422s is_ubo on Particular), Notes. Role-scoped
 * fields reset on role change so a stale flag can't ride the payload.
 * Specialist linkage (which holder a representative acts for, beneficiary
 * subtype, POA reference) stays on the row's own detail page after Save.
 *
 * Because the agreement's client_type drives UBO visibility and is not known
 * until an agreement is picked, it is fetched via useAgreement on the
 * selected id (unlike AddAgreementPartyModal where it is a prop).
 *
 * C-113: the modal renders inside PersonDetailPage's <form id="person-form">
 * subtree (Mantine portals don't stop React event propagation), so the inner
 * submit must stopPropagation or it ALSO fires the page Save.
 */
import {
  Alert, Button, Group, Modal, Select, Stack, Switch, Text, Textarea,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import * as React from 'react';

import { AgreementPicker } from './EntityPicker';
import { useAgreement } from '../hooks/useAgreements';
import type {
  AgreementPartyCreateInput, PartyRole, RepresentativePower, UUID,
} from '../api/types';

const ROLE_OPTS: { value: PartyRole; label: string }[] = [
  { value: 'HOLDER', label: 'Holder' },
  { value: 'REPRESENTATIVE', label: 'Representative' },
  { value: 'BENEFICIARY', label: 'Beneficiary' },
];

const POWER_OPTS: { value: RepresentativePower; label: string }[] = [
  { value: 'LPOA', label: 'LPOA (natural-person holder)' },
  { value: 'MANDATO', label: 'Mandato (legal-entity holder)' },
];

interface FormValues {
  agreement_id: string;
  role: PartyRole;
  is_ubo: boolean;
  representative_power_type: string;
  notes: string;
}

const EMPTY: FormValues = {
  agreement_id: '',
  role: 'HOLDER',
  is_ubo: false,
  representative_power_type: '',
  notes: '',
};

interface Props {
  opened: boolean;
  onClose: () => void;
  /** The person this role attaches — fixed (this page). Shown read-only at
   *  the top of the modal for confirmation. */
  personId: UUID;
  personLabel: string;
  /** Called with the built payload. The parent appends a CREATE op with a
   *  freshly-minted tempKey to its pending-party queue. */
  onStage: (payload: AgreementPartyCreateInput) => void;
}

export function AddPersonAgreementRoleModal({
  opened, onClose, personId, personLabel, onStage,
}: Props) {
  const form = useForm<FormValues>({
    initialValues: EMPTY,
    validate: {
      agreement_id: (v) => (v.length >= 32 ? null : 'Agreement required'),
      representative_power_type: (v, values) =>
        values.role === 'REPRESENTATIVE' && !v
          ? 'Required for representatives'
          : null,
    },
  });

  // Clean slate on every open — dismiss = discard (§3g modal semantics).
  React.useEffect(() => {
    if (opened) form.setValues(EMPTY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // The picked agreement's client_type drives UBO visibility (the backend
  // 422s is_ubo on a Particular agreement — ubo_requires_empresa guard).
  const agreementQ = useAgreement(form.values.agreement_id || undefined);
  const clientType = agreementQ.data?.client_type;

  const isBeneficiary = form.values.role === 'BENEFICIARY';
  const isRepresentative = form.values.role === 'REPRESENTATIVE';
  const uboAllowed = isBeneficiary && clientType === 'Empresa';

  function handleSave(values: FormValues) {
    const payload: AgreementPartyCreateInput = {
      agreement_id: values.agreement_id,
      person_id: personId,
      role: values.role,
      // C-115: always sent explicitly (the RM pending-path response stub
      // 500'd on a sparse payload; the server also defaults it now).
      // Gated on uboAllowed (role BENEFICIARY + Empresa) so a UBO flag set
      // before re-picking a Particular agreement can't ride the payload and
      // trip the backend's ubo_requires_empresa 422.
      is_ubo: uboAllowed && values.is_ubo,
    };
    if (values.role === 'REPRESENTATIVE' && values.representative_power_type) {
      payload.representative_power_type =
        values.representative_power_type as RepresentativePower;
    }
    if (values.notes) payload.notes = values.notes;
    onStage(payload);
    onClose();
  }

  // C-113: stop the inner submit bubbling to the page's person-form.
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    form.onSubmit(handleSave)(e);
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Add an agreement role for this person" size="lg">
      <form onSubmit={handleFormSubmit}>
        <Stack>
          <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm" fw={500}>{personLabel}</Text>
            <Text size="xs" c="dimmed">
              The new role is staged and only commits when you click Save
              Changes on the person page. Specialist linkage (which holder a
              representative acts for, beneficiary subtype, POA reference) can
              be refined on the role&apos;s own page after Save.
            </Text>
          </Alert>

          <AgreementPicker
            label="Agreement"
            description="Pick the agreement to attach this person to in the selected role."
            value={form.values.agreement_id || null}
            onChange={(v) => form.setFieldValue('agreement_id', v ?? '')}
            error={form.errors.agreement_id as string | undefined}
          />

          <Select
            label="Role"
            data={ROLE_OPTS}
            value={form.values.role}
            onChange={(v) => {
              if (!v) return;
              form.setFieldValue('role', v as PartyRole);
              // Role-scoped fields reset when the role changes so a stale
              // UBO flag / power type can't ride along on the payload.
              form.setFieldValue('is_ubo', false);
              form.setFieldValue('representative_power_type', '');
            }}
            allowDeselect={false}
            required
          />

          {isRepresentative && (
            <Select
              label="Power type"
              description="LPOA for natural-person holders, Mandato for legal-entity holders."
              data={POWER_OPTS}
              value={form.values.representative_power_type || null}
              onChange={(v) => form.setFieldValue('representative_power_type', v ?? '')}
              error={form.errors.representative_power_type as string | undefined}
              required
            />
          )}

          {uboAllowed && (
            <Switch
              label="UBO (ultimate beneficial owner)"
              checked={form.values.is_ubo}
              onChange={(e) => form.setFieldValue('is_ubo', e.currentTarget.checked)}
            />
          )}

          <Textarea
            label="Notes"
            placeholder="Optional"
            value={form.values.notes}
            onChange={(e) => form.setFieldValue('notes', e.currentTarget.value)}
            autosize
            minRows={2}
          />

          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button type="submit">Stage role</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
